import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import AppContext from 'context/Context';
import Logo from 'components/common/Logo';
import NavbarTopDropDownMenus from './NavbarTopDropDownMenus';
import TopNavRightSideNavItem from './TopNavRightSideNavItem';
import { useLocation } from 'react-router-dom';
import PebBreadcrumb from "components/layouts/breadcrumb/PebBreadCrumb";
import HeaderSearch from './HeaderSearch';
import { useBreadcrumbState } from 'context/BreadcrumbContext';
import '../navbar-tw.scss';

// Collapsible menu region for top/combo/double-top navbar positions.
// Replaces bootstrap `.collapse.navbar-collapse` + `.navbar-expand-lg`:
// hidden on mobile unless toggled, always visible from lg (1024px) up.
const topCollapseClass = navbarCollapsed =>
  classNames(
    'tw-basis-full tw-grow tw-order-1 tw-overflow-auto tw-max-h-[calc(100dvh-var(--topnav-height))]',
    'lg:tw-block lg:tw-basis-auto lg:tw-grow-0 lg:tw-order-none lg:tw-overflow-visible lg:tw-max-h-none',
    navbarCollapsed ? 'tw-block' : 'tw-hidden'
  );

const NavbarTop = () => {
  const {
    config: { showBurgerMenu, navbarPosition, navbarCollapsed },
    setConfig
  } = useContext(AppContext);

  const { pathname } = useLocation();
  const isChat = pathname.includes('chat');

  const [showDropShadow, setShowDropShadow] = useState(false);

  const handleBurgerMenu = () => {
    (navbarPosition === 'top' || navbarPosition === 'double-top') &&
      setConfig('navbarCollapsed', !navbarCollapsed);
    (navbarPosition === 'vertical' || navbarPosition === 'combo') &&
      setConfig('showBurgerMenu', !showBurgerMenu);
  };

  const setDropShadow = () => {
    const el = document.documentElement;
    if (el.scrollTop > 0) {
      setShowDropShadow(true);
    } else {
      setShowDropShadow(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', setDropShadow);
    return () => window.removeEventListener('scroll', setDropShadow);
  }, []);

  const burgerMenuRef = useRef();

  return (
    <nav
      className={classNames('ptl-navbar-top tw-text-sm', {
        'ptl-navbar-top--shadow': showDropShadow && !isChat
      })}
    >
      {navbarPosition === 'double-top' ? (
        <div className="tw-w-full">
          <div className="tw-flex tw-justify-between tw-items-center">
            <NavbarTopElements
              navbarCollapsed={navbarCollapsed}
              navbarPosition={navbarPosition}
              handleBurgerMenu={handleBurgerMenu}
              burgerMenuRef={burgerMenuRef}
            />
          </div>
          <hr className="tw-my-2 tw-hidden lg:tw-block" />
          <div className={classNames(topCollapseClass(navbarCollapsed), 'tw-py-2')}>
            <div className="tw-flex tw-flex-col lg:tw-flex-row">
              <NavbarTopDropDownMenus />
            </div>
          </div>
        </div>
      ) : (
        <NavbarTopElements
          navbarCollapsed={navbarCollapsed}
          navbarPosition={navbarPosition}
          handleBurgerMenu={handleBurgerMenu}
          burgerMenuRef={burgerMenuRef}
        />
      )}
    </nav>
  );
};

const NavbarTopElements = ({
  navbarPosition,
  handleBurgerMenu,
  navbarCollapsed
}) => {
  const burgerMenuRef = useRef();
  const { searchVisible } = useBreadcrumbState();

  // navbarBreakPoint ('xl') for vertical/combo, topNavbarBreakpoint ('lg') for
  // top/double-top — enumerated statically so tailwind can see the classes.
  const isTopPosition =
    navbarPosition === 'top' || navbarPosition === 'double-top';

  return (
    <>
      <div
        ref={burgerMenuRef}
        className={classNames(
          'ptl-navbar-toggler tw-mr-2 md:tw-mr-3',
          isTopPosition ? 'lg:tw-hidden' : 'xl:tw-hidden'
        )}
      >
        <button
          className="ptl-hamburger"
          onClick={handleBurgerMenu}
          id="burgerMenu"
        >
          <span className="ptl-navbar-toggle-icon">
            <span className="ptl-toggle-line" />
          </span>
        </button>
      </div>

      {/* At >= xl the vertical sidebar shows its own logo, so hide this one */}
      <div className={isTopPosition ? '' : 'xl:tw-hidden'}>
        <Logo
          at="navbar-top"
          width={52}
          id="topLogo"
          className="tw-flex tw-items-center"
          textClass="tw-text-lg tw-font-bold"
          style={{ textDecoration: 'none' }}
        />
      </div>

      <PebBreadcrumb className="tw-hidden md:tw-block md:tw-ml-4 tw-mb-0 tw-flex-grow" />

      {searchVisible && (
        // No longer hidden on a phone — the component now renders an icon-only
        // variant below `md` and the pill above it.
        <HeaderSearch className="tw-mr-1 md:tw-mr-3" />
      )}

      {navbarPosition === 'top' || navbarPosition === 'combo' ? (
        <div className={classNames(topCollapseClass(navbarCollapsed), 'tw-pb-3 lg:tw-pb-0')}>
          <div className="tw-flex tw-flex-col lg:tw-flex-row">
            <NavbarTopDropDownMenus />
          </div>
        </div>
      ) : (
        <></>
      )}
      <TopNavRightSideNavItem />
    </>
  );
};

NavbarTopElements.propTypes = {
  navbarPosition: PropTypes.string,
  handleBurgerMenu: PropTypes.func,
  navbarCollapsed: PropTypes.bool
};
export default NavbarTop;
